import { gql } from '@apollo/client';
import {
	TariffViewModel
} from '@/graphql/generated';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { GenericAutocomplete, AutocompleteConfig } from './generic-autocomplete';
import { TariffCreateForm } from './tariff-create-form';

export interface TariffAutocompleteProps {
	value: string;
	onChange: (value: string, tariff: TariffViewModel | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	enableCreateNew?: boolean;
	initialSelectedLabel?: string; // Display label to show when form loads with a selected value
}

// GraphQL query for tariffs
const TARIFFS_QUERY = gql`
	query TariffsList(
		$where: TariffViewModelFilterInput
		$first: Int
		$after: String
	) {
		tariffs(where: $where, first: $first, after: $after) {
			edges {
				node {
					tariffId
					name
					facility {
						name
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

// Helper functions for tariff display
export const getTariffDisplayName = (tariff: TariffViewModel): string => {
	return tariff.name || 'Unnamed Tariff';
};

export const getTariffSecondaryText = (tariff: TariffViewModel): string => {
	// Show facility name as secondary text
	if (tariff.facility?.name) {
		return `Facility: ${tariff.facility.name}`;
	}
	return '';
};

// Create tariff autocomplete configuration
const createTariffAutocompleteConfig = (enableCreateNew = false): AutocompleteConfig<TariffViewModel> => ({
	query: TARIFFS_QUERY,
	queryName: 'tariffs',
	searchFields: ['name'],
	getDisplayName: getTariffDisplayName,
	getSecondaryText: getTariffSecondaryText,
	getId: (tariff) => tariff.tariffId,
	pageSize: DEFAULT_PAGE_SIZE,
	// Enable details functionality
	enableDetails: true,
	entityType: 'tariffs',
	createNew: enableCreateNew ? {
		enabled: true,
		entityName: 'Tariff',
		createFormComponent: TariffCreateForm,
	} : undefined,
});

export const TariffAutocomplete: React.FC<TariffAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Search tariffs...',
	disabled = false,
	className = '',
	enableCreateNew = false,
	initialSelectedLabel
}) => {
	const config = createTariffAutocompleteConfig(enableCreateNew);

	return (
		<GenericAutocomplete<TariffViewModel>
			config={config}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			disabled={disabled}
			className={className}
			initialSelectedLabel={initialSelectedLabel}
		/>
	);
};
